import { StatusLegendMinWidth } from '../components/StatusLegend'
import {
  CardMargin,
  CHART_CARD_CHART_MARGIN,
  CHART_CARD_CHART_VIEW_MARGIN,
  CHART_CARD_TABLE_MARGIN,
  sizeToMinCardHeight,
  sizeToMinCardWidth,
  sizeToMinChartHeight,
  sizeToMinChartWidth
} from '../constants'
import type { ChartCardChartContainerSizeProps, ChartCardTableContainerSizeProps } from '../internalTypes'
import type {
  ChartCardActionContext,
  ChartCardActionOnClick,
  ChartCardCardsProps,
  ChartCardGridSize,
  ChartCardType,
  ChartDataRow
} from '../types'
import { assertNever, getWidthAndHeightFromSizeProps } from './generics'

export const getChartCardChartContainerSize = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  view,
  cardSize,
  chartSize
}: ChartCardChartContainerSizeProps<DataKey, DataRow>): {
  size: ChartCardGridSize
  cardMinWidth: number
  cardMinHeight: number
  containerMinWidth: number
  containerMinHeight: number
  containerWidth: number | `${number}%`
  containerHeight: number | `${number}%`
  chartWidth: number
  chartHeight: number
} => {
  let chartWidth: number | undefined
  let chartHeight: number | undefined
  let cardMinWidth: number | undefined
  let cardMinHeight: number | undefined

  const viewMargins = CHART_CARD_CHART_VIEW_MARGIN[view]

  const horzMargin =
    (viewMargins?.left ?? CHART_CARD_CHART_MARGIN.left) + (viewMargins?.right ?? CHART_CARD_CHART_MARGIN.right)
  const vertMargin =
    (viewMargins?.top ?? CHART_CARD_CHART_MARGIN.top) + (viewMargins?.bottom ?? CHART_CARD_CHART_MARGIN.bottom)

  if (chartSize != null) {
    ;({ width: chartWidth, height: chartHeight } = getWidthAndHeightFromSizeProps(
      view,
      chartSize,
      sizeToMinChartWidth,
      sizeToMinChartHeight
    ))
  }

  if (cardSize != null) {
    ;({ width: cardMinWidth, height: cardMinHeight } = getWidthAndHeightFromSizeProps(
      view,
      cardSize,
      sizeToMinCardWidth,
      sizeToMinCardHeight
    ))
  }

  const size = chartSize?.size ?? cardSize?.size ?? 'full'

  const polarLegendWidth = view === 'polar' ? StatusLegendMinWidth : 0
  chartWidth ??= (cardMinWidth ?? sizeToMinCardWidth[size]) - CardMargin * 2 - horzMargin - polarLegendWidth
  chartHeight ??= (cardMinHeight ?? sizeToMinCardHeight[size]) - CardMargin * 2 - vertMargin

  cardMinWidth ??= chartWidth + CardMargin * 2 + horzMargin + polarLegendWidth
  cardMinHeight ??= chartHeight + CardMargin * 2 + vertMargin

  const containerWidthFromCard = cardSize?.width == null ? undefined : cardSize.width - CardMargin * 2
  const containerWidthFromChart = chartSize?.width == null ? undefined : chartSize.width + horzMargin + polarLegendWidth
  const containerMinWidth = containerWidthFromCard ?? containerWidthFromChart ?? cardMinWidth - CardMargin * 2
  const containerMinHeight = cardMinHeight - CardMargin * 2
  const containerWidth = containerWidthFromCard ?? '100%'
  const containerHeight = containerMinHeight

  return {
    size,
    cardMinWidth,
    cardMinHeight,
    containerMinWidth,
    containerMinHeight,
    containerWidth,
    containerHeight,
    chartWidth,
    chartHeight
  }
}

export const getChartCardTableContainerSize = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  view,
  cardSize,
  tableSize
}: ChartCardTableContainerSizeProps<DataKey, DataRow>): {
  size: ChartCardGridSize
  containerMinWidth: number
  containerMinHeight: number
  containerWidth: number | `${number}%`
  containerHeight: number | `${number}%`
  cardWidth: number
  cardHeight: number
} => {
  let tableWidth: number | undefined
  let tableHeight: number | undefined
  let cardWidth: number | undefined
  let cardHeight: number | undefined

  const viewMargins = CHART_CARD_CHART_VIEW_MARGIN[view]

  const horzMargin =
    (viewMargins?.left ?? CHART_CARD_TABLE_MARGIN.left) + (viewMargins?.right ?? CHART_CARD_TABLE_MARGIN.right)
  const vertMargin =
    (viewMargins?.top ?? CHART_CARD_TABLE_MARGIN.top) + (viewMargins?.bottom ?? CHART_CARD_TABLE_MARGIN.bottom)

  if (tableSize != null) {
    ;({ width: tableWidth, height: tableHeight } = getWidthAndHeightFromSizeProps(
      view,
      tableSize,
      sizeToMinCardWidth,
      sizeToMinCardHeight
    ))
  }

  if (cardSize != null) {
    ;({ width: cardWidth, height: cardHeight } = getWidthAndHeightFromSizeProps(
      view,
      cardSize,
      sizeToMinCardWidth,
      sizeToMinCardHeight
    ))
  }

  const size = tableSize?.size ?? cardSize?.size ?? 'full'

  tableWidth ??= (cardWidth ?? sizeToMinCardWidth[size]) - horzMargin
  tableHeight ??= (cardHeight ?? sizeToMinCardHeight[size]) - vertMargin

  cardWidth = tableWidth + horzMargin
  cardHeight = tableHeight + vertMargin

  const containerMinWidth = cardWidth
  const containerMinHeight = cardHeight // - paginationHeight
  const containerWidth = cardSize?.width ?? '100%'
  const containerHeight = containerMinHeight

  return { size, containerMinWidth, containerMinHeight, containerWidth, containerHeight, cardWidth, cardHeight }
}

export const getActionClickHandler = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  id: string,
  onClick: ChartCardActionOnClick<DataKey, DataRow, ChartCardActionContext<DataKey, DataRow>>,
  getContext: () => ChartCardActionContext<DataKey, DataRow> | null
): (() => void) => {
  return () => {
    const context = getContext()
    if (context != null) {
      onClick(id, context)
    }
  }
}

const getOtherCardTypes = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  cardTypeSelected,
  cardProp: { chart, performance },
  allowChangeCardType
}: {
  cardTypeSelected: ChartCardType
  cardProp: ChartCardCardsProps<DataKey, DataRow>
  allowChangeCardType: boolean | undefined
}): ChartCardType[] => {
  const otherCardTypes: ChartCardType[] = []
  if (allowChangeCardType === true) {
    if (cardTypeSelected !== 'table') {
      otherCardTypes.push('table')
    }
    if (chart != null && cardTypeSelected !== 'chart') {
      otherCardTypes.push('chart')
    }
    if (performance != null && cardTypeSelected !== 'performance') {
      otherCardTypes.push('performance')
    }
  }

  return otherCardTypes
}

export const validateChangeCardType = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  cardTypeSelected,
  cardProp,
  allowChangeCardType
}: {
  cardTypeSelected: ChartCardType
  cardProp: ChartCardCardsProps<DataKey, DataRow>
  allowChangeCardType: boolean | undefined
}): {
  card: ChartCardCardsProps<DataKey, DataRow>
  otherCardTypes: ChartCardType[]
} => {
  const { chart, performance } = cardProp

  switch (cardTypeSelected) {
    case 'table':
      return {
        card: {
          ...cardProp,
          type: 'table'
        },
        otherCardTypes: getOtherCardTypes({ cardTypeSelected: 'table', cardProp, allowChangeCardType })
      }
    case 'chart':
      if (chart == null) {
        return {
          card: cardProp,
          otherCardTypes: getOtherCardTypes({ cardTypeSelected: cardProp.type, cardProp, allowChangeCardType })
        }
      }
      return {
        card: {
          ...cardProp,
          type: 'chart',
          chart
        },
        otherCardTypes: getOtherCardTypes({ cardTypeSelected: 'chart', cardProp, allowChangeCardType })
      }
    case 'performance':
      if (performance == null) {
        return {
          card: cardProp,
          otherCardTypes: getOtherCardTypes({ cardTypeSelected: cardProp.type, cardProp, allowChangeCardType })
        }
      }
      return {
        card: {
          ...cardProp,
          type: 'performance',
          performance
        },
        otherCardTypes: getOtherCardTypes({ cardTypeSelected: 'performance', cardProp, allowChangeCardType })
      }
    default:
      assertNever(cardTypeSelected)
      return {
        card: cardProp,
        otherCardTypes: []
      }
  }
}
